const MINUTES_PER_HOUR = 60;
const LAST_MINUTE_OF_DAY = 23 * MINUTES_PER_HOUR + 59;

export type SlotInterval = {
  start_time: string;
  end_time: string | null;
};

function toHhmm(value: string): string {
  return value.slice(0, 5);
}

export function timeToMinutes(value: string): number {
  const hhmm = toHhmm(value);
  const hours = Number(hhmm.slice(0, 2));
  const minutes = Number(hhmm.slice(3, 5));
  return hours * MINUTES_PER_HOUR + minutes;
}

/** Default end when enabling a slot range: start + 1h, clamped to the same calendar day. */
export function addOneHourSameDay(hhmm: string): string {
  const total = Math.min(timeToMinutes(hhmm) + MINUTES_PER_HOUR, LAST_MINUTE_OF_DAY);
  const hours = Math.floor(total / MINUTES_PER_HOUR);
  const minutes = total % MINUTES_PER_HOUR;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Soft-warn interval overlap using half-open ranges [start, end).
 * Missing end_time is a point at start. Touching endpoints do not overlap:
 * 08:00–10:00 overlaps 09:30, but not 10:00 or 10:00–12:00.
 */
export function slotsIntervalsOverlap(a: SlotInterval, b: SlotInterval): boolean {
  const aStart = timeToMinutes(a.start_time);
  const bStart = timeToMinutes(b.start_time);
  const aEndTime = a.end_time;
  const bEndTime = b.end_time;
  const aEnd = aEndTime != null ? timeToMinutes(aEndTime) : aStart;
  const bEnd = bEndTime != null ? timeToMinutes(bEndTime) : bStart;

  if (aEndTime == null && bEndTime == null) {
    return aStart === bStart;
  }
  if (aEndTime == null) {
    return aStart >= bStart && aStart < bEnd;
  }
  if (bEndTime == null) {
    return bStart >= aStart && bStart < aEnd;
  }
  return aStart < bEnd && bStart < aEnd;
}
